const toOptions = (items: string[]) =>
  items.map(item => ({ label: item, value: item.toLowerCase().replace(/\s/g, "_") }));

const programmingLanguages = [
  "Java",
  "Python",
  "JavaScript",
  "C#",
  "R",
  "Ruby",
  "Go",
  "Kotlin",
  "Swift",
  "C",
  "C++",
  "Apex",
  "Clojure",
  "Cobol",
  "Node.js",
  "Perl",
  "Rust",
  "Scala",
  "TypeScript",
  "PHP",
  "Elixir",
  "Other",
]
  .sort((a, b) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)))
  .map(language => ({ label: language, value: language.toLowerCase().replace(/\s/g, "_") }));

export const DeveloperProfileConstants = {
  KEYS: {
    JOB_FUNCTION: "jobFunction",
    EXPERIENCE: "experience",
    EXPERTISE: "expertise",
    LANGUAGES: "language",
    TOPICS: "topics",
    GITHUB_USERNAME: "githubUsername",
    OTHER_LANGUAGE: "otherLanguage",
    OTHER_TOPIC: "otherTopic",
  },

  OPTIONS: {
    jobRoles: [
      { label: "Backend Web Developer", value: "backend_web_developer" },
      { label: "Cloud Engineer", value: "cloud_engineer" },
      { label: "Customer Success", value: "customer_success" },
      { label: "Data Scientist", value: "data_scientist" },
      { label: "DevSecOps", value: "devsecops" },
      { label: "Frontend Web Developer", value: "frontend_web_developer" },
      { label: "Full-Stack Developer", value: "full_stack_developer" },
      { label: "HR", value: "hr" },
      { label: "Legal", value: "legal" },
      { label: "Marketing", value: "marketing" },
      { label: "Mobile Developer", value: "mobile_developer" },
      { label: "Native Developer", value: "native_developer" },
      { label: "Non-Developer", value: "business_learner" },
      { label: "Operations", value: "operations" },
      { label: "Privacy Engineer", value: "privacy_engineer" },
      { label: "Product Manager", value: "product_manager" },
      { label: "Sales", value: "sales" },
      { label: "Tester", value: "tester" },
      { label: "Other", value: "other" },
    ],

    experienceLevels: [
      { label: "0-2 years of experience", value: "beginner" },
      { label: "3-5 years of experience", value: "intermediate" },
      { label: "5-10 years of experience", value: "advanced" },
      { label: "11+ years of experience", value: "experienced" },
    ],

    technicalExpertise: [
      { label: "I read or write code regularly", value: "reads_writes_code_regularly" },
      {
        label: "I understand code but do not write it",
        value: "understands_code_but_does_not_write_it",
      },
      { label: "I don't work directly with code", value: "does_not_work_directly_with_code" },
    ],

    programmingLanguages,

    // the additional-languages multi-select has no "Other" free-text input
    additionalLanguages: programmingLanguages.filter(language => language.value !== "other"),

    architectureTools: [
      "Azure",
      "GCP",
      "AWS",
      "Docker",
      "Kubernetes",
      "Terraform",
      "Embedded",
      "Other",
    ].map(tool => ({ label: tool, value: tool.toLowerCase() })),

    aiToolCategories: [
      {
        key: "assistants",
        label: "Tools",
        placeholder: "Select tool(s)",
        options: toOptions([
          "ChatGPT",
          "ChatGPT Codex",
          "Claude",
          "Claude Code",
          "Claude Cowork",
          "Cursor",
          "GitHub Copilot",
          "JetBrains AI Assistant",
          "Microsoft Copilot",
          "OpenAI Codex",
          "OpenClaw",
        ]),
      },
      {
        key: "models",
        label: "Models",
        placeholder: "Select model(s)",
        options: toOptions([
          "Claude",
          "DeepSeek",
          "Gemini",
          "GPT",
          "Grok",
          "Llama",
          "Mistral",
          "Qwen",
        ]),
      },
      {
        key: "frameworks",
        label: "Frameworks",
        placeholder: "Select framework(s)",
        options: toOptions([
          "DSPy",
          "Haystack",
          "LangChain",
          "LangGraph",
          "LlamaIndex",
          "Spring AI",
          "Vercel AI SDK",
        ]),
      },
      {
        key: "platforms",
        label: "Infrastructure",
        placeholder: "Select infrastructure",
        options: toOptions([
          "Amazon Bedrock",
          "Google Vertex AI",
          "Groq",
          "Hugging Face",
          "Ollama",
          "OpenRouter",
          "vLLM",
        ]),
      },
    ],
  },

  VALUES: {
    EXPERTISE_DOES_NOT_WORK_WITH_CODE: "does_not_work_directly_with_code",
    DOES_NOT_USE_AI: "i_do_not_use_ai",
    OTHER: "other",
  },

  TEXT: {
    HEADER: "Developer Profile",
    INTRO_MESSAGE: "Help us customize your experience by answering a few questions:",
    JOB_FUNCTION_LABEL:
      "What best describes your day-to-day job function within your organization?",
    EXPERIENCE_LABEL: "How many years of development experience do you have?",
    EXPERTISE_LABEL: "Which of the following best describes your technical expertise?",
    LANGUAGES_LABEL: "Which programming language or technology do you use most in your daily work?",
    TOPICS_LABEL: "Which best describes the architecture and tools/topics that you work on?",
    GITHUB_LABEL: "What is your GitHub username? (Optional)",
    JOB_FUNCTION_PLACEHOLDER: "Select Job Function",
    EXPERIENCE_PLACEHOLDER: "Select Experience Level",
    EXPERTISE_PLACEHOLDER: "Select Expertise",
    LANGUAGES_PLACEHOLDER: "Select Language",
    TOPICS_PLACEHOLDER: "Select Tool(s)",
    VALIDATION_MESSAGE: "Please make a selection to submit an answer.",
    OTHER_LABEL: "Please specify: ",
    OTHER_LANGUAGE_PLACEHOLDER: "Enter your language",
    OTHER_TOPIC_PLACEHOLDER: "Enter your topic",
  },
};
